"use client";

import { useInfront } from "@/context/infront-context";
import {
  createInstrumentSlug,
  DEFAULT_CONTENT,
} from "@/services/infront/utils";
import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

const CURRENCY_FLAG_MAP: Record<string, string> = {
  CADSEK: "ca",
  CNYSEK: "cn",
  DKKSEK: "dk",
  EURSEK: "eu",
  GBPSEK: "gb",
  JPYSEK: "jp",
  NOKSEK: "no",
  USDSEK: "us",
  EURGBP: "eu",
  EURUSD: "eu",
  GBPUSD: "gb",
  USDCAD: "us",
  USDCHF: "ch",
  USDCNY: "us",
  USDHKD: "us",
  USDJPY: "us",
};

function applyFlagMap(container: HTMLElement) {
  container.querySelectorAll('span[aria-label="TICKER"]').forEach((el) => {
    const text = el.textContent?.trim();
    const flag = text ? CURRENCY_FLAG_MAP[text] : undefined;
    if (!flag) return;

    const flagEl = el.previousElementSibling as HTMLElement | null;
    if (flagEl?.classList.contains("cell-flag")) {
      flagEl.className = `cell-flag cell-flag-${flag}`;
      flagEl.title = flag.toUpperCase();
    }
  });
}

type CurrencyListProps = {
  id: string;
  title?: string;
  currencies: { feed: number; ticker: string }[];
};

const CurrencyList = (props: CurrencyListProps) => {
  const router = useRouter();
  const { isReady, load } = useInfront();

  const widgetRef = useRef<any>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    let active = true;

    load?.(["core", "highstock", "hollowcandlestick", "heikinashi"]).then(() => {
      if (!isReady || !active) return;

      widgetRef.current?.destroy();
      widgetRef.current = null;
      observerRef.current?.disconnect();

      const widgetOptions = new window.Infront.QuoteListWidgetOptions();

      widgetOptions.onInstrumentSelected = (instrument: {
        feed: number;
        ticker: string;
      }) => {
        router.push(createInstrumentSlug(instrument.feed, instrument.ticker));
      };

      widgetOptions.instruments = props.currencies.map(
        (currency) =>
          new window.Infront.Instrument(currency.feed, currency.ticker)
      );

      widgetOptions.defaultContent = DEFAULT_CONTENT;
      widgetOptions.enableChangeStatusColors = true;
      widgetOptions.widgetTitle = props.title;

      widgetOptions.columns = [
        { name: "TICKER", flag: true },
        "LAST",
        "CHANGE",
        "PCT_CHANGE",
        "TIME",
      ];

      widgetRef.current = window.infront.quoteList(`#${props.id}`, widgetOptions);

      const container = document.getElementById(props.id);
      if (container) {
        observerRef.current = new MutationObserver(() => applyFlagMap(container));
        observerRef.current.observe(container, { childList: true, subtree: true, characterData: true });
      }
    });

    return () => {
      active = false;
      widgetRef.current?.destroy();
      widgetRef.current = null;
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [isReady, router, props.id, props.currencies, props.title]);

  return <div className="card" id={props.id} />;
};

export default CurrencyList;
